import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowLeft, FileText, Loader2, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RecordConsents } from '@/components/RecordConsents';
import { useToast } from '@/components/hooks/use-toast';
import {
  fetchApplicationConsentStatusByClientPolicyApplicationIdRequest,
  postConsentRecordRequest,
  selectApplicationConsentStatusData,
  selectApplicationConsentStatusIsLoading,
  selectPostConsentRecordData,
  selectPostConsentRecordErrorMessage,
  clearPostConsentRecord,
} from '@client/model';
import { FundingPolicyType } from '@shared';
import { useClientPolicyApplication } from '@/hooks/useClientPolicyApplication';
import { getUserIpAddress } from '@/util/getUserIpAddress';
import { DownloadCarePacketButton } from '@/components/policy-application/DownloadCarePacketButton';

type UserType = 'advisor' | 'client' | 'partnerClient';
type ConsentType = 'submit' | 'start';

export function RecordConsentsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const { policyApplication } = useClientPolicyApplication();

  const clientPolicyApplicationId = searchParams.get('clientPolicyApplicationId');
  const userType = searchParams.get('userType') as UserType;
  const consentType = searchParams.get('consentType') as ConsentType;

  const consentStatusData = useSelector(selectApplicationConsentStatusData);
  const isLoadingStatus = useSelector(selectApplicationConsentStatusIsLoading);
  const postConsentRecordData = useSelector(selectPostConsentRecordData);
  const postConsentRecordError = useSelector(selectPostConsentRecordErrorMessage);

  const [batchSubmission, setBatchSubmission] = useState({
    isActive: false,
    total: 0,
    successful: 0,
    failed: 0,
  });

  const isValidParams = useMemo(() => {
    return (
      clientId &&
      clientPolicyApplicationId &&
      userType === 'partnerClient' && // Page only visible for partnerClient
      ['start', 'submit'].includes(consentType) // Page only visible to get start/submit consent type
    );
  }, [clientId, clientPolicyApplicationId, consentType, userType]);

  // Check if user has pending consents
  const isPending = useMemo(() => {
    if (!consentStatusData) return true;

    // Check based on id if current user's consent is pending
    if (consentStatusData.allConsents.client.userId === clientId) {
      return consentStatusData.allConsents.client.isPending;
    } else if (
      consentStatusData.allConsents.partnerClient &&
      consentStatusData.allConsents.partnerClient.userId === clientId
    ) {
      return consentStatusData.allConsents.partnerClient.isPending;
    }
  }, [clientId, consentStatusData]);

  const fetchConsentStatus = useCallback(() => {
    if (clientPolicyApplicationId) {
      dispatch(
        fetchApplicationConsentStatusByClientPolicyApplicationIdRequest({
          clientPolicyApplicationId,
          queryParams: { consentType },
        }),
      );
    }
  }, [clientPolicyApplicationId, dispatch, consentType]);

  // Redirect if invalid params
  useEffect(() => {
    if (!isValidParams) {
      console.error('Invalid parameters for RecordConsentsPage', {
        clientId,
        clientPolicyApplicationId,
        userType,
        consentType,
      });
      navigate('/');
    }
  }, [isValidParams, navigate, clientId, clientPolicyApplicationId, userType, consentType]);

  // Fetch consent status on mount
  useEffect(() => {
    fetchConsentStatus();
  }, [fetchConsentStatus]);

  // Handle successful consent recording
  useEffect(() => {
    if (postConsentRecordData && batchSubmission.isActive) {
      setBatchSubmission(prev => ({
        ...prev,
        successful: prev.successful + 1,
      }));
      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordData, batchSubmission.isActive, dispatch]);

  // Handle consent recording errors
  useEffect(() => {
    if (postConsentRecordError && batchSubmission.isActive) {
      setBatchSubmission(prev => ({
        ...prev,
        failed: prev.failed + 1,
      }));
      dispatch(clearPostConsentRecord());
    }
  }, [postConsentRecordError, batchSubmission.isActive, dispatch]);

  // Handle batch completion
  useEffect(() => {
    if (
      batchSubmission.isActive &&
      batchSubmission.successful + batchSubmission.failed === batchSubmission.total
    ) {
      if (batchSubmission.failed === 0) {
        // All succeeded
        toast({
          title: 'Consents Recorded',
          description: 'Your consents have been successfully recorded.',
          className: 'bg-green-50 text-green-800',
        });

        // Refetch consent status
        fetchConsentStatus();
      } else {
        toast({
          title: 'Error Recording Consents',
          description: 'Failed to record your consents. Please try again.',
          className: 'bg-red-50 text-red-800',
        });
      }

      setBatchSubmission({
        isActive: false,
        total: 0,
        successful: 0,
        failed: 0,
      });
    }
  }, [batchSubmission, toast, fetchConsentStatus]);

  const handleSubmitConsents = useCallback(
    async (consentIds: string[]) => {
      if (!isPending || !clientId || !clientPolicyApplicationId) return;

      // Initialize batch tracking
      setBatchSubmission({
        isActive: true,
        total: consentIds.length,
        successful: 0,
        failed: 0,
      });

      const ipAddress = await getUserIpAddress();

      // Dispatch all consent record requests
      consentIds.forEach(consentId =>
        dispatch(
          postConsentRecordRequest({
            clientId,
            clientPolicyApplicationId,
            resourceType: 'ClientPolicyApplicationConsents',
            consentId,
            ipAddress,
            userAgent: navigator.userAgent,
            consentType,
            metadata: {
              userType,
              clientPolicyApplicationId,
            },
          }),
        ),
      );
    },
    [isPending, clientId, clientPolicyApplicationId, dispatch, userType, consentType],
  );

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!isValidParams) {
    return null;
  }

  return (
    <div className="container mx-auto max-w-4xl py-8">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={handleGoBack}
          className="mb-4 gap-2 text-gray-700 hover:text-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Go Back
        </Button>

        <div className="flex items-center gap-3">
          <div className="bg-primary-50 rounded-lg p-3">
            <FileText className="text-primary h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Record Consents</h1>
            <p className="text-gray-700">
              Complete the consent agreements for the policy application
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Consent Agreements</CardTitle>
              <p className="text-sm text-gray-700">Application ID: {clientPolicyApplicationId}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchConsentStatus}
              disabled={isLoadingStatus}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoadingStatus ? 'animate-spin' : ''}`} />
              Refresh Status
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Loading State */}
          {isLoadingStatus ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="text-primary mx-auto mb-4 h-8 w-8 animate-spin" />
                <p className="text-gray-700">Loading consent status...</p>
              </div>
            </div>
          ) : /* Submitting State */ batchSubmission.isActive ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Loader2 className="text-primary mx-auto mb-4 h-8 w-8 animate-spin" />
                <p className="text-gray-700">Recording consents...</p>
                <p className="text-sm text-gray-700">
                  {batchSubmission.successful + batchSubmission.failed} of {batchSubmission.total}{' '}
                  processed
                </p>
              </div>
            </div>
          ) : /* Already Recorded State */ !isPending ? (
            <>
              <div className="space-y-4">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex items-center">
                    <CheckCircle2 className="mr-3 h-5 w-5 text-green-600" />
                    <div>
                      <h4 className="text-sm font-medium text-green-800">
                        Consents Already Recorded
                      </h4>
                      <p className="text-sm text-green-700">
                        All required consents have been provided for this user.
                      </p>
                    </div>
                  </div>
                </div>
                {!consentStatusData?.hasRecievedCarePacket && consentType === 'start' && (
                  <div className="mt-4 flex justify-end">
                    <DownloadCarePacketButton
                      text="Download Take Home Packet"
                      applicationId={clientPolicyApplicationId!}
                      clientId={clientId!}
                      filesToDownload={consentStatusData?.carePacketFileUrls || []}
                      onDownloadComplete={() => {}}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Consent Form */
            <RecordConsents
              userType={userType}
              onSubmit={handleSubmitConsents}
              consentType={consentType}
              showPdf={
                consentType === 'start' &&
                policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
              }
              isCareScout={
                policyApplication?.fundingPolicyType === FundingPolicyType.careScoutCareAssurance
              }
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
